import PropTypes from "prop-types";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import { API_URL } from "config";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useModulePermissions } from "utils/modulePermissions";
import { useAuth } from "../../AuthContext";
import { invoiceActionPermission, invoiceWorkflowPermission } from "./invoicePermissions";

const cellSx = {
  border: "1px solid #d8dee7",
  color: "#2f2f2f",
  fontSize: "11px",
  lineHeight: 1.3,
  py: 0.5,
  px: 0.75,
};

const labelCellSx = { ...cellSx, fontWeight: 700, width: "36%" };
const companyBrandColor = "#c2185b";
const invoiceNavy = "#132c56";
const invoiceBorder = "#d8dee7";
const invoiceFontFamily = "Arial, Helvetica, sans-serif";
const companyHeaderTextColor = "#333333";
const sectionTitleSx = {
  display: "block",
  color: invoiceNavy,
  px: 0.8,
  pt: 0.45,
  pb: 0.65,
  fontSize: "11px",
  lineHeight: 1.2,
  letterSpacing: "0.01em",
  "&::after": {
    content: '""',
    display: "block",
    width: "40px",
    height: "2px",
    mt: 0.25,
    backgroundColor: companyBrandColor,
  },
};
const companyHeaderDetailsSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
};
const companyHeaderIconSx = {
  width: "18px !important",
  minWidth: "18px !important",
  height: "18px !important",
  m: "0 !important",
  p: "0 !important",
  fontSize: "17px !important",
  lineHeight: "18px !important",
  color: companyBrandColor,
  flex: "0 0 18px",
};
const companyHeaderLinesSx = {
  width: "100%",
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  rowGap: "5px",
  textAlign: "center",
  mx: "auto",
  color: companyHeaderTextColor,
  "& .MuiTypography-root": { color: `${companyHeaderTextColor} !important` },
};

const money = (value, invoice) => {
  const amount = Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const code = String(invoice?.currency_code || "USD").toUpperCase();
  const symbols = { USD: "$", INR: "₹", GBP: "£", EURO: "€", EUR: "€", AED: "د.إ", SGD: "S$" };
  const symbol =
    symbols[code] ||
    (invoice?.currency_symbol && invoice.currency_symbol !== code ? invoice.currency_symbol : code);
  return `${symbol}${amount}`;
};

const currencySymbol = (code, invoice) => {
  const normalizedCode = String(code || "USD").toUpperCase();
  const symbols = { USD: "$", INR: "₹", GBP: "£", EURO: "€", EUR: "€", AED: "د.إ", SGD: "S$" };
  return (
    symbols[normalizedCode] ||
    (invoice?.currency_symbol && invoice.currency_symbol !== normalizedCode
      ? invoice.currency_symbol
      : normalizedCode)
  );
};

const usdMoney = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const displayDate = (value) => {
  if (!value) return "";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const decimal = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const percentage = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const valuePresent = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";
const firstEmailAddress = (value) =>
  String(value || "")
    .split(/[;,\r\n]+/)
    .map((email) => email.trim())
    .filter(Boolean)[0] || "";

const statusOptions = [
  { value: "approved", label: "Approved" },
  { value: "cancelled", label: "Cancelled" },
  { value: "rejected", label: "Rejected" },
];
const paymentStatusOptions = [
  { value: "pending", label: "Payment Pending" },
  { value: "paid", label: "Paid" },
];
const emailStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
];
const selectedOption = (options, value) => options.find((option) => option.value === value) || null;
const eInvoiceCancelReasons = [
  { value: "1", label: "Wrong entry" },
  { value: "2", label: "Duplicate entry" },
  { value: "3", label: "Order cancelled" },
  { value: "4", label: "Other" },
];
const eInvoiceStatusMeta = {
  not_generated: { label: "Not generated", color: "#64748b" },
  processing: { label: "Processing", color: "#f59e0b" },
  generated: { label: "Generated", color: "#16a34a" },
  failed: { label: "Request failed", color: "#ef4444" },
  cancelled: { label: "Cancelled", color: "#ef4444" },
  verification_required: { label: "Verify in GSTZen/IRP", color: "#dc2626" },
};

const normalizeInvoiceCountry = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();
  if (["IN", "IND", "INDIA"].includes(normalized)) return "IN";
  if (["UAE", "AE", "UNITED ARAB EMIRATES"].includes(normalized)) return "UAE";
  return normalized;
};

const numberWordsBelowThousand = (number) => {
  const ones = [
    "",
    "ONE",
    "TWO",
    "THREE",
    "FOUR",
    "FIVE",
    "SIX",
    "SEVEN",
    "EIGHT",
    "NINE",
    "TEN",
    "ELEVEN",
    "TWELVE",
    "THIRTEEN",
    "FOURTEEN",
    "FIFTEEN",
    "SIXTEEN",
    "SEVENTEEN",
    "EIGHTEEN",
    "NINETEEN",
  ];
  const tens = [
    "",
    "",
    "TWENTY",
    "THIRTY",
    "FORTY",
    "FIFTY",
    "SIXTY",
    "SEVENTY",
    "EIGHTY",
    "NINETY",
  ];
  if (number < 20) return ones[number];
  if (number < 100)
    return `${tens[Math.floor(number / 10)]}${number % 10 ? ` ${ones[number % 10]}` : ""}`;
  return `${ones[Math.floor(number / 100)]} HUNDRED${
    number % 100 ? ` ${numberWordsBelowThousand(number % 100)}` : ""
  }`;
};

const numberWordsInternational = (number) => {
  const wholeNumber = Math.floor(Math.max(0, Number(number || 0)));
  if (wholeNumber < 1000) return numberWordsBelowThousand(wholeNumber);
  const scales = [
    [1000000000000, "TRILLION"],
    [1000000000, "BILLION"],
    [1000000, "MILLION"],
    [1000, "THOUSAND"],
  ];
  const [scaleValue, scaleName] = scales.find(([scale]) => wholeNumber >= scale);
  const leading = Math.floor(wholeNumber / scaleValue);
  const remainder = wholeNumber % scaleValue;
  return `${numberWordsInternational(leading)} ${scaleName}${
    remainder ? ` ${numberWordsInternational(remainder)}` : ""
  }`;
};

const numberWordsIndian = (number) => {
  const wholeNumber = Math.floor(Math.max(0, Number(number || 0)));
  if (wholeNumber < 1000) return numberWordsBelowThousand(wholeNumber);
  if (wholeNumber < 100000) {
    const remainder = wholeNumber % 1000;
    return `${numberWordsBelowThousand(Math.floor(wholeNumber / 1000))} THOUSAND${
      remainder ? ` ${numberWordsIndian(remainder)}` : ""
    }`;
  }
  if (wholeNumber < 10000000) {
    const remainder = wholeNumber % 100000;
    return `${numberWordsBelowThousand(Math.floor(wholeNumber / 100000))} LAKH${
      remainder ? ` ${numberWordsIndian(remainder)}` : ""
    }`;
  }
  const remainder = wholeNumber % 10000000;
  return `${numberWordsIndian(Math.floor(wholeNumber / 10000000))} CRORE${
    remainder ? ` ${numberWordsIndian(remainder)}` : ""
  }`;
};

const amountInWords = (value, currencyCode = "INR", useCurrencyCode = false) => {
  const code = String(currencyCode || "INR")
    .trim()
    .toUpperCase();
  const currencyUnits = {
    INR: ["RUPEES", "PAISE", "indian"],
    USD: ["US DOLLARS", "CENTS", "international"],
    GBP: ["POUNDS", "PENCE", "international"],
    EUR: ["EUROS", "CENTS", "international"],
    EURO: ["EUROS", "CENTS", "international"],
    AED: ["DIRHAMS", "FILS", "international"],
    SGD: ["SINGAPORE DOLLARS", "CENTS", "international"],
  };
  const [majorUnit, minorUnit, numberingSystem] = currencyUnits[code] || [
    code,
    "CENTS",
    "international",
  ];
  const majorPrefix = useCurrencyCode ? (code === "EURO" ? "EUR" : code) : majorUnit;
  const minorUnitTotal = Math.round(Math.max(0, Number(value || 0)) * 100);
  const majorValue = Math.floor(minorUnitTotal / 100);
  const minorValue = minorUnitTotal % 100;
  const toWords = numberingSystem === "indian" ? numberWordsIndian : numberWordsInternational;
  return `${majorPrefix} ${toWords(majorValue) || "ZERO"}${
    minorValue ? ` AND ${minorUnit} ${toWords(minorValue)}` : ""
  } ONLY`;
};

function InvoiceView({ invoiceType, documentType = "invoice" }) {
  const isVendorInvoice = invoiceType === "vendor";
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions = [], userPermission = [] } = useAuth();
  const isCreditNote = documentType === "credit_note";
  const moduleName = isCreditNote ? "credit_notes" : `invoices.${invoiceType}`;
  const apiBase = isCreditNote ? "/credit-notes" : `/invoices/${invoiceType}`;
  const vendorId = invoiceType === "vendor"
    ? Number(new URLSearchParams(location.search).get("vendor_id") || 0)
    : 0;
  const vendorInvoiceQuery = invoiceType === "vendor" && vendorId > 0 ? `?vendor_id=${vendorId}` : "";
  const listPath = isCreditNote ? "/credit-notes" : `/invoices/${invoiceType}`;
  const documentLabel = isCreditNote ? "Credit Note" : "Invoice";
  const documentLabelLower = documentLabel.toLowerCase();
  const pagePermission = (page) => `${moduleName}.pages.${page}.view`;
  const actionPermission = (action) =>
    isCreditNote ? `credit_notes.actions.${action}` : invoiceActionPermission(invoiceType, action);
  const workflowPermission = (action) =>
    isCreditNote ? `credit_notes.actions.${action}` : invoiceWorkflowPermission(invoiceType, action);
  const { hasModulePermission, canViewField } = useModulePermissions(
    permissions,
    userPermission,
    moduleName
  );
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [workflowStatuses, setWorkflowStatuses] = useState({
    status: "approved",
    payment_status: "pending",
    email_status: "pending",
  });
  const [workflowSaving, setWorkflowSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [eInvoiceBusy, setEInvoiceBusy] = useState(false);
  const [generateDialog, setGenerateDialog] = useState({ open: false, inr_exchange_rate: "" });
  const [cancelDialog, setCancelDialog] = useState({ open: false, reason_code: "1", remarks: "" });
  const [workflowSnackbar, setWorkflowSnackbar] = useState({
    open: false,
    color: "success",
    message: "",
  });

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        if (invoiceType === "vendor" && vendorId <= 0) {
          throw new Error("Please open the vendor invoice from the vendor invoice list.");
        }
        const response = await fetch(`${API_URL}${apiBase}/${id}${vendorInvoiceQuery}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok || result.success === false)
          throw new Error(result.message || `Unable to load ${documentLabelLower}.`);
        setInvoice(result.data);
        setWorkflowStatuses({
          status: result.data?.status || "approved",
          payment_status: result.data?.payment_status || "pending",
          email_status: result.data?.email_status || "pending",
        });
      } catch (requestError) {
        if (requestError.name !== "AbortError")
          setError(requestError.message || `Unable to load ${documentLabelLower}.`);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [apiBase, documentLabelLower, id, invoiceType, vendorId, vendorInvoiceQuery]);

  const company = invoice?.invoicing_company || {};
  const companyNameWords = String(company.name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const companyNameAccent = companyNameWords.length > 1 ? companyNameWords.pop() : "";
  const companyNamePrimary = companyNameWords.join(" ") || String(company.name || "").trim();
  const canViewInvoiceField = (field) => canViewField(field);
  const canViewCompanyDetails = canViewInvoiceField("company_details");
  const canViewQrCode = canViewInvoiceField("qr_code");
  const canViewItems = canViewInvoiceField("items");
  const canViewEInvoiceDetails = !isCreditNote && canViewInvoiceField("e_invoice_details");
  const canViewAmountSummary = [
    "usd_amount",
    "exchange_rate",
    "total_exchange_amount",
    "cgst_amount",
    "sgst_amount",
    "igst_amount",
    "total_tax",
    "final_amount",
  ].some((field) => canViewInvoiceField(field));
  const partyLabel = invoiceType === "client" ? "Client" : "Vendor";
  const currencyCode = String(invoice?.currency_code || "USD").toUpperCase();
  const isUsd = currencyCode === "USD";
  const currencySign = currencySymbol(currencyCode, invoice);
  const eInvoiceSourceAmountUsd = Number(invoice?.source_amount_usd || 0) > 0
    ? Number(invoice.source_amount_usd)
    : (invoice?.items || []).reduce((sum, item) => sum + (Number(item?.total) || 0), 0);
  const invoiceExchangeRate = Number(invoice?.exchange_rate || 0);
  const useInvoiceInrRate = currencyCode === "INR" && invoiceExchangeRate > 0;
  const eInvoiceInrRate = useInvoiceInrRate
    ? invoiceExchangeRate
    : Number(generateDialog.inr_exchange_rate || 0);
  const eInvoiceConvertedAmount = useInvoiceInrRate
    ? Number(invoice?.total_amount || 0)
    : eInvoiceSourceAmountUsd * eInvoiceInrRate;
  const validEInvoiceInrRate =
    eInvoiceInrRate > 0 &&
    /^\d{1,3}(?:\.\d{1,7})?$/.test(
      String(useInvoiceInrRate ? invoiceExchangeRate : generateDialog.inr_exchange_rate).trim()
    );
  const invoiceQrEInvoiceStatus = String(invoice?.e_invoice_status || "").toLowerCase();
  const eInvoiceSignedQrCode = String(invoice?.e_invoice_signed_qr_code || "").trim();
  const hasGeneratedQrEInvoice = ["generated", "cancelled"].includes(invoiceQrEInvoiceStatus);
  const qrValue = JSON.stringify({
    document_type: documentType,
    document_number: invoice?.credit_note_number || invoice?.invoice_number,
    document_date: invoice?.credit_note_date || invoice?.invoice_date,
    invoice_type: invoice?.invoice_type || invoiceType,
    issuer: invoice?.invoicing_company?.name,
    billed_to: invoice?.billing_name,
    total_amount: invoice?.total_amount,
    currency: invoice?.currency_code,
  });
  const effectiveQrValue = eInvoiceSignedQrCode || (hasGeneratedQrEInvoice ? "" : qrValue);
  const fullCompanyAddress = String(company.address || "").trim();
  const companyAddressParts = fullCompanyAddress.match(/^(.*?\bGurugram Road\b)(.*)$/i);
  const companyLocationLines = companyAddressParts
    ? [companyAddressParts[1], companyAddressParts[2].replace(/^[,\s]+/, "")].filter(valuePresent)
    : [fullCompanyAddress].filter(valuePresent);
  const additionalHsn = (company.additional_company_fields || []).find((item) =>
    /hsn/i.test(String(item?.name || ""))
  );
  const companyHsn = company.hsn_code || additionalHsn?.value || "";
  const bankRows = (invoiceType === "vendor"
    ? (invoice?.vendor_bank_details || []).map((item) => [item?.name, item?.value])
    : [
        ["Bank Name", company.bank_name],
        ["Account Holder", company.account_holder],
        ["A/C No.", company.account_number],
        ["IFSC Code", company.ifsc_code],
        ["SWIFT Code", company.swift_code],
        ...(company.additional_bank_fields || []).map((item) => [item.name, item.value]),
      ]
  ).filter(([name, value]) => valuePresent(name) && valuePresent(value));
  const billingTaxRows = invoice?.party?.taxes || [];
  const gstinTax = billingTaxRows.find((tax) => /gstin|gst/i.test(String(tax.name || "")));
  const panTax = billingTaxRows.find((tax) => /pan/i.test(String(tax.name || "")));
  const otherBillingTaxRows = billingTaxRows.filter((tax) => tax !== gstinTax && tax !== panTax);
  // Client tax details are independent of the invoicing-company blank-field rules.
  // Accept both the canonical API keys and the explicit display aliases.
  const clientGstin =
    [invoice?.party?.gst_number, invoice?.party?.gstin, gstinTax?.number].find(valuePresent) || "";
  const clientPan =
    [invoice?.party?.pan_number, invoice?.party?.pan, panTax?.number].find(valuePresent) || "";
  const clientState = valuePresent(invoice?.billing_state)
    ? String(invoice.billing_state).trim()
    : "";
  const clientCountry = valuePresent(invoice?.billing_country)
    ? String(invoice.billing_country).trim()
    : String(invoice?.party?.country || "").trim();
  const clientStateCountry = [clientState, clientCountry].filter(valuePresent).join(" / ");
  const companyCountry = normalizeInvoiceCountry(company.country);
  const clientCountryCode = normalizeInvoiceCountry(clientCountry);
  const clientIsForeign = valuePresent(clientCountry) && clientCountryCode !== "IN";
  const showCompanyLut =
    companyCountry === "IN" &&
    (clientIsForeign || Boolean(invoice?.party?.se_zone)) &&
    valuePresent(company.lut_number);
  const companyAdditionalRows = (company.additional_company_fields || [])
    .filter(
      (item) =>
        !/hsn|pan/i.test(String(item?.name || "")) &&
        valuePresent(item?.name) &&
        valuePresent(item?.value)
    )
    .map((item) => ({ label: item.name, value: item.value }));
  const companyIdentityRows = (
    companyCountry !== "IN" && companyAdditionalRows.length > 0
      ? companyAdditionalRows
      : showCompanyLut
        ? [{ label: "LUT (ARN) No.", value: company.lut_number }]
        : []
  ).filter((row) => valuePresent(row.value));
  const issuerDetailRows = [
    ...(isCreditNote
      ? [
          {
            field: "credit_note_number",
            label: "Credit Note No.",
            value: invoice?.credit_note_number || invoice?.invoice_number,
          },
          {
            field: "credit_note_date",
            label: "Credit Note Date",
            value: displayDate(invoice?.credit_note_date || invoice?.invoice_date),
          },
          {
            field: "source_invoice_number",
            label: "Source Invoice No.",
            value: (invoice?.source_invoice_numbers || []).join(", "),
          },
        ]
      : [
          { field: "invoice_number", label: invoiceType === "vendor" ? "PO Number" : "Invoice No.", value: invoice?.invoice_number },
          { field: "invoice_date", label: invoiceType === "vendor" ? "PO Date" : "Invoice Date", value: displayDate(invoice?.invoice_date) },
          ...(invoiceType !== "vendor" ? [{ field: "due_date", label: "Due Date", value: displayDate(invoice?.due_date) }] : []),
          ...(invoiceType !== "vendor" ? [{
            field: "reference",
            label: invoiceType === "client" ? "Client PO" : "Vendor Reference",
            value: invoice?.reference_number,
          }] : []),
        ]),
    ...(invoiceType !== "vendor"
      ? [{ field: "hsn_code", label: "HSN Code", value: invoice?.hsn_code || companyHsn }]
      : []),
    ...companyIdentityRows.map((row) => ({ ...row, field: "company_details" })),
  ].filter((row) => canViewInvoiceField(row.field) && valuePresent(row.value));
  const billedPartyRows = [
    {
      field: "billing_name",
      label: "Name / Company",
      value: invoice?.billing_name || invoice?.party?.name,
    },
    { field: "billing_email", label: "Email", value: invoice?.billing_email },
    { field: "billing_address", label: "Address", value: invoice?.billing_address },
    { field: "billing_address", label: "City", value: invoice?.billing_city },
    { field: "billing_postal_code", label: "ZIP / Postal Code", value: invoice?.billing_postal_code },
    { field: "billing_state_country", label: "State / Country", value: clientStateCountry },
    {
      field: "billing_tax_details",
      label: "GST Code",
      value: invoiceType === "client" ? invoice?.party?.state_code : "",
    },
    { field: "billing_tax_details", label: "GSTIN", value: clientGstin },
    { field: "billing_tax_details", label: "PAN Number", value: clientPan },
    { field: "billing_currency", label: "Billing Currency", value: invoice?.currency_code },
    ...(invoiceType !== "client"
      ? otherBillingTaxRows.map((tax) => ({
          field: "billing_tax_details",
          label: tax.name,
          value: tax.number,
        }))
      : []),
  ].filter((row) => canViewInvoiceField(row.field) && valuePresent(row.value));
  const invoiceValue = Number(invoice?.total_amount || 0);
  const showIndianTaxBreakup =
    companyCountry === "IN" &&
    clientCountryCode === "IN" &&
    !Boolean(invoice?.party?.se_zone) &&
    Boolean(invoice?.tax_enabled);
  const indianTaxBreakupRows = !showIndianTaxBreakup
    ? []
    : invoice?.tax_rule === "india_same_state"
    ? [
        {
          field: "cgst_amount",
          label: "CGST (9%)",
          displayValue: `${currencySign}${decimal(invoice?.cgst_amount)}`,
        },
        {
          field: "sgst_amount",
          label: "SGST (9%)",
          displayValue: `${currencySign}${decimal(invoice?.sgst_amount)}`,
        },
      ]
    : invoice?.tax_rule === "india_interstate"
    ? [
        {
          field: "igst_amount",
          label: "IGST (18%)",
          displayValue: `${currencySign}${decimal(invoice?.igst_amount)}`,
        },
      ]
    : [
        Number(invoice?.cgst_amount || 0) > 0 && {
          field: "cgst_amount",
          label: "CGST (9%)",
          displayValue: `${currencySign}${decimal(invoice?.cgst_amount)}`,
        },
        Number(invoice?.sgst_amount || 0) > 0 && {
          field: "sgst_amount",
          label: "SGST (9%)",
          displayValue: `${currencySign}${decimal(invoice?.sgst_amount)}`,
        },
        Number(invoice?.igst_amount || 0) > 0 && {
          field: "igst_amount",
          label: "IGST (18%)",
          displayValue: `${currencySign}${decimal(invoice?.igst_amount)}`,
        },
      ].filter(Boolean);
  const foreignCompanyTaxPercentage = Number(
    invoice?.tax_percentage || company.tax_percentage || 0
  );
  const showForeignCompanyTax =
    companyCountry !== "IN" &&
    Boolean(company.enable_tax) &&
    Boolean(invoice?.tax_enabled) &&
    foreignCompanyTaxPercentage > 0;
  const totalTaxPercentage = showIndianTaxBreakup
    ? Number(invoice?.tax_percentage || 18)
    : showForeignCompanyTax
    ? foreignCompanyTaxPercentage
    : 0;
  const taxSummaryRows = invoiceType === "vendor"
    ? []
    : [
        ...indianTaxBreakupRows,
        {
          field: "total_tax",
          label: `Total Tax (${percentage(totalTaxPercentage)}%)`,
          displayValue: `${currencySign}${decimal(invoice?.tax_amount)}`,
        },
      ];
  const amountSummaryRows = [
    ...(!isUsd ? [{
      field: "exchange_rate",
      label: "Exchange Rate",
      displayValue: `1 USD = ${currencySign}${decimal(invoice?.exchange_rate)}`,
    }] : []),
    ...(invoiceType !== "vendor" ? [{
      field: "total_exchange_amount",
      label: "Amount",
      displayValue: `${currencySign}${decimal(invoice?.subtotal)}`,
    }] : []),
    ...taxSummaryRows,
    {
      field: "final_amount",
      label: `FINAL AMOUNT (${currencyCode})`,
      displayValue: `${currencySign}${decimal(invoice?.total_amount)}`,
    },
  ].filter((row) => canViewInvoiceField(row.field));
  const jurisdiction = company.state || company.country || "our jurisdiction";
  const canReturnToList = hasModulePermission(pagePermission("list"));
  const canSendEmail =
    hasModulePermission(actionPermission("send_email")) && hasModulePermission(pagePermission("view"));
  const emailSendCount = Math.max(0, Number(invoice?.email_send_count || 0));
  const emailButtonLabel = emailSendCount > 0
    ? `Resend Email (${emailSendCount})`
    : `Send Email ${documentLabel}`;
  const canDownloadPdf =
    hasModulePermission(actionPermission("download_pdf")) && hasModulePermission(pagePermission("view"));
  const eInvoicePdfUrl = (() => {
    if (
      invoiceType !== "client" ||
      !canViewQrCode ||
      !canViewEInvoiceDetails ||
      !canDownloadPdf ||
      invoiceQrEInvoiceStatus !== "generated"
    ) return null;
    try {
      const url = new URL(String(invoice?.e_invoice_pdf_url || "").trim());
      return url.protocol === "https:" ? url.href : null;
    } catch {
      return null;
    }
  })();
  const eInvoiceStatus = invoice?.e_invoice_status || "not_generated";
  const eInvoiceActionState = invoice?.e_invoice_action_state || {};
  const isIndianInvoicingCountry =
    typeof eInvoiceActionState.country_eligible === "boolean"
      ? eInvoiceActionState.country_eligible
      : ["IN", "INDIA"].includes(String(invoice?.invoice_country || "").trim().toUpperCase()) &&
        ["IN", "INDIA"].includes(String(company.country || "").trim().toUpperCase());
  const eInvoiceMeta = eInvoiceStatusMeta[eInvoiceStatus] || {
    label: eInvoiceStatus,
    color: "#f59e0b",
  };
  const eInvoiceLocked =
    typeof eInvoiceActionState.locked === "boolean"
      ? eInvoiceActionState.locked
      : ["processing", "generated", "cancelled", "verification_required"].includes(
          eInvoiceStatus
        );
  const canGenerateEInvoice =
    !isCreditNote &&
    invoiceType === "client" &&
    isIndianInvoicingCountry &&
    hasModulePermission(invoiceActionPermission(invoiceType, "generate_e_invoice")) &&
    hasModulePermission(`invoices.${invoiceType}.pages.view.view`) &&
    (typeof eInvoiceActionState.generate_available === "boolean"
      ? eInvoiceActionState.generate_available
      : !["generated", "cancelled", "processing", "verification_required"].includes(
          eInvoiceStatus
        ));
  const canCancelEInvoice =
    !isCreditNote &&
    invoiceType === "client" &&
    hasModulePermission(invoiceActionPermission(invoiceType, "cancel_e_invoice")) &&
    hasModulePermission(`invoices.${invoiceType}.pages.view.view`) &&
    (typeof eInvoiceActionState.cancel_available === "boolean"
      ? eInvoiceActionState.cancel_available
      : eInvoiceStatus === "generated");
  const canUpdateWorkflowField = (field) => {
    const action = {
      status: "update_status",
      payment_status: "update_payment_status",
      email_status: "update_email_status",
    }[field];
    return Boolean(action) &&
      hasModulePermission(workflowPermission(action)) &&
      hasModulePermission(pagePermission("view")) &&
      canViewInvoiceField(field);
  };
  const canUpdateWorkflow = ["status", "payment_status", "email_status"].some(canUpdateWorkflowField);
  const availableEmailStatusOptions = canSendEmail || workflowStatuses.email_status === "sent"
    ? emailStatusOptions
    : emailStatusOptions.filter((option) => option.value !== "sent");
  const workflowDirty = Boolean(invoice) && [
    ["status", invoice?.status || "approved"],
    ["payment_status", invoice?.payment_status || "pending"],
    ["email_status", invoice?.email_status || "pending"],
  ].some(([field, currentValue]) => (
    canUpdateWorkflowField(field) && workflowStatuses[field] !== currentValue
  ));
  const changeWorkflowStatus = (field, option) => {
    if (!option?.value) return;
    setWorkflowStatuses((current) => ({ ...current, [field]: option.value }));
  };
  const updateWorkflowStatuses = async () => {
    if (!invoice || !canUpdateWorkflow || !workflowDirty) return;

    const currentStatuses = {
      status: invoice?.status || "approved",
      payment_status: invoice?.payment_status || "pending",
      email_status: invoice?.email_status || "pending",
    };
    const changedStatuses = Object.fromEntries(Object.entries(workflowStatuses).filter(
      ([field, value]) => canUpdateWorkflowField(field) && value !== currentStatuses[field]
    ));
    if (Object.keys(changedStatuses).length === 0) return;

    setWorkflowSaving(true);
    try {
      const response = await fetch(`${API_URL}${apiBase}/${id}/workflow-status${vendorInvoiceQuery}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(changedStatuses),
      });
      const result = await response.json();
      if (!response.ok || result.success === false) {
        if (result.data) setInvoice(result.data);
        const validationMessage = Object.values(result.errors || {})
          .flat()
          .find(Boolean);
        throw new Error(
          validationMessage || result.message || `Unable to update ${documentLabelLower} statuses.`
        );
      }

      setInvoice(result.data);
      setWorkflowStatuses({
        status: result.data?.status || "approved",
        payment_status: result.data?.payment_status || "pending",
        email_status: result.data?.email_status || "pending",
      });
      setWorkflowSnackbar({
        open: true,
        color: "success",
        message: `${documentLabel} statuses updated successfully.`,
      });
    } catch (requestError) {
      setWorkflowSnackbar({
        open: true,
        color: "error",
        message: requestError.message || `Unable to update ${documentLabelLower} statuses.`,
      });
    } finally {
      setWorkflowSaving(false);
    }
  };
  const sendInvoiceEmail = async () => {
    if (!invoice || !canSendEmail || sendingEmail) return;

    setSendingEmail(true);
    try {
      const response = await fetch(`${API_URL}${apiBase}/${id}/workflow-status${vendorInvoiceQuery}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email_status: "sent", send_email: true }),
      });
      const result = await response.json();
      if (!response.ok || result.success === false) {
        if (result.data) setInvoice(result.data);
        const validationMessage = Object.values(result.errors || {})
          .flat()
          .find(Boolean);
        throw new Error(
          validationMessage || result.message || `Unable to send ${documentLabelLower} email.`
        );
      }

      setInvoice(result.data);
      setWorkflowStatuses({
        status: result.data?.status || "approved",
        payment_status: result.data?.payment_status || "pending",
        email_status: result.data?.email_status || "sent",
      });
      setWorkflowSnackbar({
        open: true,
        color: "success",
        message: result.message || `${documentLabel} email sent successfully.`,
      });
    } catch (requestError) {
      setWorkflowSnackbar({
        open: true,
        color: "error",
        message: requestError.message || `Unable to send ${documentLabelLower} email.`,
      });
    } finally {
      setSendingEmail(false);
    }
  };
  const downloadInvoicePdf = async () => {
    if (!invoice || downloadingPdf) return;

    setDownloadingPdf(true);
    try {
      const response = await fetch(`${API_URL}${apiBase}/${id}/pdf${vendorInvoiceQuery}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          Accept: "application/pdf",
        },
      });
      if (!response.ok) {
        let message = `Unable to download ${documentLabelLower} PDF.`;
        try {
          const result = await response.json();
          message = result.message || message;
        } catch {
          // Keep the generic message when the API returns a non-JSON error response.
        }
        throw new Error(message);
      }

      const pdfBlob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      const invoiceFileName =
        String(
          invoice.credit_note_number || invoice.invoice_number || `${documentLabelLower}-${id}`
        )
          .replace(/[^A-Za-z0-9._-]+/g, "-")
          .replace(/^-+|-+$/g, "") || `${documentLabelLower}-${id}`;
      link.href = downloadUrl;
      link.download = `${invoiceFileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (requestError) {
      setWorkflowSnackbar({
        open: true,
        color: "error",
        message: requestError.message || `Unable to download ${documentLabelLower} PDF.`,
      });
    } finally {
      setDownloadingPdf(false);
    }
  };
  const openGenerateEInvoiceDialog = () => {
    if (invoice && !isIndianInvoicingCountry) {
      setWorkflowSnackbar({ open: true, color: "error", message: "GSTZen e-invoice generation is available only when our invoicing country is India (IN)." });
      return;
    }
    if (!invoice || !canGenerateEInvoice || eInvoiceBusy) return;
    const initialRate = useInvoiceInrRate
      ? String(invoiceExchangeRate)
      : String(invoice?.egen_exchange_rate || "");
    setGenerateDialog({ open: true, inr_exchange_rate: initialRate });
  };
  const generateEInvoice = async () => {
    if (invoice && !isIndianInvoicingCountry) {
      setWorkflowSnackbar({ open: true, color: "error", message: "GSTZen e-invoice generation is available only when our invoicing country is India (IN)." });
      return;
    }
    if (!invoice || !canGenerateEInvoice || eInvoiceBusy) return;
    if (!generateDialog.open || !validEInvoiceInrRate) {
      setWorkflowSnackbar({
        open: true,
        color: "error",
        message: "Please enter a valid USD-to-INR exchange rate.",
      });
      return;
    }

    setEInvoiceBusy(true);
    try {
      const response = await fetch(`${API_URL}/invoices/${invoiceType}/${id}/e-invoice/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          Accept: "application/json",
        },
        body: JSON.stringify({ inr_exchange_rate: eInvoiceInrRate }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        if (result.data) setInvoice(result.data);
        const validationMessage = Object.values(result.errors || {})
          .flat()
          .find(Boolean);
        throw new Error(validationMessage || result.message || "Unable to generate e-invoice.");
      }
      setInvoice(result.data);
      setGenerateDialog({ open: false, inr_exchange_rate: "" });
      setWorkflowSnackbar({
        open: true,
        color: "success",
        message: result.message || "E-invoice generated successfully.",
      });
    } catch (requestError) {
      setWorkflowSnackbar({
        open: true,
        color: "error",
        message: requestError.message || "Unable to generate e-invoice.",
      });
    } finally {
      setEInvoiceBusy(false);
    }
  };
  const cancelEInvoice = async () => {
    if (!invoice || !canCancelEInvoice || eInvoiceBusy || !cancelDialog.remarks.trim()) return;

    setEInvoiceBusy(true);
    try {
      const response = await fetch(`${API_URL}/invoices/${invoiceType}/${id}/e-invoice/cancel`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          reason_code: cancelDialog.reason_code,
          remarks: cancelDialog.remarks.trim(),
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        if (result.data) setInvoice(result.data);
        const validationMessage = Object.values(result.errors || {})
          .flat()
          .find(Boolean);
        throw new Error(validationMessage || result.message || "Unable to cancel e-invoice.");
      }
      setInvoice(result.data);
      setCancelDialog({ open: false, reason_code: "1", remarks: "" });
      setWorkflowSnackbar({
        open: true,
        color: "success",
        message: result.message || "E-invoice cancelled successfully.",
      });
    } catch (requestError) {
      setWorkflowSnackbar({
        open: true,
        color: "error",
        message: requestError.message || "Unable to cancel e-invoice.",
      });
    } finally {
      setEInvoiceBusy(false);
    }
  };
  const printInvoice = () => {
    const pageTitle = document.title;
    const restorePageTitle = () => {
      document.title = pageTitle;
      window.removeEventListener("afterprint", restorePageTitle);
    };
    const invoiceFileName =
      String(
        invoice?.credit_note_number || invoice?.invoice_number || `${documentLabelLower}-${id}`
      )
        .replace(/[^A-Za-z0-9._-]+/g, "-")
        .replace(/^-+|-+$/g, "") || `${documentLabelLower}-${id}`;
    document.title = invoiceFileName;
    window.addEventListener("afterprint", restorePageTitle);
    window.print();
  };
  const itemColumns =
    isCreditNote
      ? [
          { field: "serial", label: "#", width: "4%", value: (_item, index) => index + 1 },
          {
            field: "item_source_invoice",
            label: "Source Invoice",
            width: "15%",
            value: (item) => item.source_invoice_number || "-",
          },
          {
            field: "item_project_code",
            label: "Project Code",
            width: "12%",
            value: (item) => item.project_code || "-",
          },
          {
            field: "item_country",
            label: "Country",
            width: "11%",
            value: (item) => item.country || "-",
          },
          {
            field: "item_description",
            label: "Description of Services",
            width: "26%",
            value: (item) => item.description || "-",
          },
          { field: "item_quantity", label: "Qty", width: "7%", value: (item) => item.quantity },
          {
            field: "item_rate",
            label: "Rate (USD)",
            width: "11%",
            value: (item) => usdMoney(item.rate),
          },
          {
            field: "item_amount",
            label: "Amount (USD)",
            width: "14%",
            value: (item) => usdMoney(item.total),
            align: "right",
          },
        ]
      : invoiceType === "client"
      ? [
          { field: "serial", label: "#", width: "5%", value: (_item, index) => index + 1 },
          {
            field: "item_project_code",
            label: "Project Code",
            width: "13%",
            value: (item) => item.project_code || "-",
          },
          {
            field: "item_country",
            label: "Country",
            width: "12%",
            value: (item) => item.country || "-",
          },
          {
            field: "item_description",
            label: "Description of Services",
            width: "29%",
            value: (item) => item.description || "-",
          },
          { field: "item_quantity", label: "Qty", width: "8%", value: (item) => item.quantity },
          {
            field: "item_rate",
            label: "Rate (USD)",
            width: "15%",
            value: (item) => usdMoney(item.rate),
          },
          {
            field: "item_amount",
            label: "Amount (USD)",
            width: "18%",
            value: (item) => usdMoney(item.total),
            align: "right",
          },
        ]
      : [
          { field: "serial", label: "#", width: "5%", value: (_item, index) => index + 1 },
          {
            field: "item_project_code",
            label: "Project Code",
            width: "20%",
            value: (item) => item.project_code || "-",
          },
          {
            field: "item_country",
            label: "Country",
            width: "15%",
            value: (item) => item.country || "-",
          },
          // Temporarily hidden for vendor invoices as requested.
          // {
          //   field: "item_description",
          //   label: "Description of Services",
          //   width: "29%",
          //   value: (item) => item.description || "-",
          // },
          { field: "item_quantity", label: "Completes", width: "18%", value: (item) => item.quantity },
          {
            field: "item_rate",
            label: "Vendor CPI (USD)",
            width: "20%",
            value: (item) => usdMoney(item.rate),
          },
          {
            field: "item_amount",
            label: "Amount (USD)",
            width: "22%",
            value: (item) => usdMoney(item.total),
            align: "right",
          },
        ];
  const visibleItemColumns = canViewItems
    ? itemColumns.filter((column) => canViewInvoiceField(column.field))
    : [];
  const invoiceItems = invoice?.items || [];
  const totalCompletes = invoiceItems.reduce(
    (total, item) => total + Number(item?.quantity || 0),
    0
  );
  const totalQuantityDisplay = totalCompletes.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  const quantityColumnIndex = visibleItemColumns.findIndex(
    (column) => column.field === "item_quantity"
  );
  const amountColumnIndex = visibleItemColumns.findIndex(
    (column) => column.field === "item_amount"
  );
  const calculationSummaryRows = amountSummaryRows;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <style>{`
        .invoice-print-header { display: none; }
        @media print {
          @page { size: A4 portrait; margin: 6mm; }
          body * { visibility: hidden !important; }
          #invoice-print-area, #invoice-print-area * { visibility: visible !important; }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            min-width: 0 !important;
            height: auto !important;
            overflow: visible !important;
            background: #fff !important;
          }
          #invoice-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 3mm !important;
            box-sizing: border-box !important;
            box-shadow: none !important;
            break-inside: avoid-page !important;
            page-break-inside: avoid !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          #invoice-print-area .invoice-header-grid { grid-template-columns: minmax(0, 1fr) !important; align-items: center !important; padding: 0 !important; gap: 0 !important; }
          #invoice-print-area .invoice-header-grid.invoice-header-with-logo { grid-template-columns: 150px minmax(0, 1fr) !important; }
          #invoice-print-area .invoice-header-grid.invoice-header-with-qr { grid-template-columns: minmax(0, 1fr) 120px !important; }
          #invoice-print-area .invoice-header-grid.invoice-header-with-logo.invoice-header-with-qr { grid-template-columns: 150px minmax(0, 1fr) 120px !important; }
          #invoice-print-area .invoice-screen-header { display: none !important; }
          #invoice-print-area .invoice-print-header {
            display: table !important;
            width: 100% !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
            border-spacing: 0 !important;
          }

#invoice-print-area .invoice-print-header td {
  border: 0 !important;
  padding: 0 !important;
  vertical-align: middle !important;
  text-align: center !important;
  position: relative !important;
}
          #invoice-print-area .invoice-print-header > tbody > tr:first-child > td {
            padding-bottom: 3mm !important;
          }
          #invoice-print-area .invoice-print-header-logo { width: 20% !important; text-align: center !important; }
          #invoice-print-area .invoice-print-header-details { width: 60% !important; text-align: center !important; padding: 7px 5px !important; }


          #invoice-print-area .invoice-print-header-logo img {
            display: inline-block !important;
            width: 109.6875px !important;
            height: 73.125px !important;
            margin: 0 auto !important;
            object-fit: contain !important;
          }


          #invoice-print-area .invoice-print-header-qr-frame {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  margin: 0 auto !important;
  width: fit-content !important;
}

#invoice-print-area .invoice-print-header-qr-frame svg,
#invoice-print-area .invoice-print-header-qr-frame img {
  display: block !important;
  width: 97.5px !important;
  height: 97.5px !important;
  margin: 0 auto !important;
}
          #invoice-print-area .invoice-print-header-rule td {
            height: 3px !important;
            background: linear-gradient(90deg, ${invoiceNavy} 0 42%, ${companyBrandColor} 42% 100%) !important;
          }
          #invoice-print-area .invoice-header-logo { padding: 0 !important; align-self: center !important; }
          #invoice-print-area .invoice-header-logo img { width: 109.6875px !important; height: 73.125px !important; }
          #invoice-print-area .invoice-header-details { padding: 7px 5px !important; }
          #invoice-print-area .invoice-header-details .MuiTypography-root { font-size: 10px !important; line-height: 1.35 !important; }
          #invoice-print-area .invoice-header-details h3.MuiTypography-root { font-size: 20px !important; line-height: 1.05 !important; margin: 0 0 4px !important; }
          #invoice-print-area .invoice-header-details .invoice-company-identity .MuiTypography-root { font-size: 8.5px !important; line-height: 1.25 !important; }
          #invoice-print-area .invoice-header-details .MuiIcon-root { font-size: 14px !important; width: 15px !important; min-width: 15px !important; height: 15px !important; line-height: 15px !important; }
          #invoice-print-area .invoice-header-qr {
            padding: 3px !important;
            align-self: center !important;
            justify-self: stretch !important;
            justify-content: center !important;
            align-items: center !important;
            text-align: center !important;
          }
          #invoice-print-area .invoice-header-qr > * { margin-left: auto !important; margin-right: auto !important; }
          #invoice-print-area .invoice-header-qr svg,
          #invoice-print-area .invoice-header-qr img { width: 97.5px !important; height: 97.5px !important; }
          #invoice-print-area .invoice-details-grid { grid-template-columns: 1fr 1fr !important; }
          #invoice-print-area .invoice-two-column-grid { grid-template-columns: 1fr 1fr !important; align-items: start !important; }
          #invoice-print-area .invoice-footer-grid { grid-template-columns: 1fr 1fr 0.85fr !important; }
          #invoice-print-area .invoice-footer-grid > div { border-right: 1px solid #d8dee7 !important; }
          #invoice-print-area .invoice-footer-grid > div:last-child { border-right: 0 !important; }
          #invoice-print-area table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; }
          #invoice-print-area table td, #invoice-print-area table th { white-space: normal !important; overflow-wrap: anywhere !important; word-break: break-word !important; }

          #invoice-print-area .invoice-item-col-serial { width: 5% !important; }
          #invoice-print-area .invoice-item-col-item_project_code { width: 16% !important; }
          #invoice-print-area .invoice-item-col-item_description { width: 34% !important; }
          #invoice-print-area .invoice-item-col-item_quantity { width: 9% !important; }
          #invoice-print-area .invoice-item-col-item_rate { width: 16% !important; }
          #invoice-print-area .invoice-item-col-item_amount { width: 20% !important; }
          ${invoiceType === "vendor" ? `
          #invoice-print-area .invoice-item-col-item_project_code { width: 20% !important; }
          #invoice-print-area .invoice-item-col-item_country { width: 15% !important; }
          #invoice-print-area .invoice-item-col-item_quantity { width: 18% !important; }
          #invoice-print-area .invoice-item-col-item_rate { width: 20% !important; }
          #invoice-print-area .invoice-item-col-item_amount { width: 22% !important; }
          ` : ""}
          #invoice-print-area, #invoice-print-area > * { max-width: 100% !important; overflow-x: hidden !important; }
          #invoice-print-area .invoice-signature-grid img { max-height: 55px !important; }
          #invoice-print-area .invoice-header-grid,
          #invoice-print-area .invoice-details-grid,
          #invoice-print-area .invoice-two-column-grid,
          #invoice-print-area .invoice-footer-grid,
          #invoice-print-area table,
          #invoice-print-area tr,
          #invoice-print-area img { break-inside: avoid-page !important; page-break-inside: avoid !important; }
          .invoice-no-print { display: none !important; }
          #invoice-print-area .invoice-billing-email-all { display: none !important; }
          #invoice-print-area .invoice-billing-email-first { display: inline !important; }
        }
      `}</style>
      <SoftBox my={2}>
        {loading ? (
          <SoftBox py={8} display="flex" justifyContent="center">
            <CircularProgress color="info" />
          </SoftBox>
        ) : error ? (
          <SoftTypography color="error">{error}</SoftTypography>
        ) : (
          invoice && (
            <>
              <SoftBox
                className="invoice-no-print"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                {canReturnToList && <SoftButton size="small" color="secondary" onClick={() => navigate(listPath)}>
                  <Icon>arrow_back</Icon>&nbsp; {isCreditNote ? "Credit Notes" : "Invoices"}
                </SoftButton>}
                <SoftBox display="flex" gap={0.75}>
                  {(isCreditNote || invoiceType === "client") && !eInvoiceLocked &&
                    hasModulePermission(actionPermission("edit")) &&
                    hasModulePermission(pagePermission("edit")) && (
                      <SoftButton
                        size="small"
                        color="info"
                        onClick={() => navigate(isCreditNote ? `/credit-notes/${id}/edit` : `/invoices/${invoiceType}/${id}/edit`)}
                      >
                        <Icon>edit</Icon>&nbsp; Edit
                      </SoftButton>
                    )}
                  {hasModulePermission(actionPermission("print")) && (
                    <SoftButton size="small" color="dark" onClick={printInvoice}>
                      <Icon>print</Icon>&nbsp; Print
                    </SoftButton>
                  )}
                  {canSendEmail && (
                    <SoftButton
                      size="small"
                      color="info"
                      disabled={sendingEmail}
                      onClick={sendInvoiceEmail}
                    >
                      <Icon>email</Icon>&nbsp; {sendingEmail ? "Sending..." : emailButtonLabel}
                    </SoftButton>
                  )}
                  {canDownloadPdf && (
                    <SoftButton
                      size="small"
                      color="error"
                      disabled={downloadingPdf}
                      onClick={downloadInvoicePdf}
                    >
                      <Icon>download</Icon>&nbsp; {downloadingPdf ? "Preparing..." : "Download PDF"}
                    </SoftButton>
                  )}
                  {canGenerateEInvoice && (
                    <SoftButton
                      size="small"
                      color="success"
                      disabled={eInvoiceBusy}
                      onClick={openGenerateEInvoiceDialog}
                    >
                      <Icon>cloud_upload</Icon>&nbsp;{" "}
                      {eInvoiceBusy ? "Preparing..." : "Generate E-Invoice"}
                    </SoftButton>
                  )}
                  {canCancelEInvoice && (
                    <SoftButton
                      size="small"
                      color="warning"
                      disabled={eInvoiceBusy}
                      onClick={() => setCancelDialog((current) => ({ ...current, open: true }))}
                    >
                      <Icon>cancel</Icon>&nbsp; Cancel E-Invoice
                    </SoftButton>
                  )}
                </SoftBox>
              </SoftBox>

              <Card
                id="invoice-print-area"
                sx={{
                  p: { xs: 1, md: 1.25 },
                  color: "#2f2f2f",
                  width: "100%",
                  maxWidth: "980px",
                  mx: "auto",
                  border: `1px solid ${invoiceNavy}`,
                  borderRadius: 0,
                  boxShadow: "none",
                  fontFamily: invoiceFontFamily,
                  "& .MuiTypography-root, & .MuiTableCell-root": {
                    fontFamily: `${invoiceFontFamily} !important`,
                  },
                }}
              >
                <table className="invoice-print-header" aria-hidden="true">
                  <tbody>
                    <tr>
                      <td className="invoice-print-header-logo">
                        {canViewCompanyDetails && company.logo && <img src={company.logo} alt="" />}
                      </td>
                      <td className="invoice-print-header-details">
                        {canViewCompanyDetails && (
                          <SoftBox
                            className="invoice-header-details"
                            sx={{ ...companyHeaderDetailsSx, py: 1, px: 1.25 }}
                          >
                            {company.name && (
                              <SoftTypography
                                variant="h3"
                                fontWeight="bold"
                                sx={{ color: invoiceNavy, lineHeight: 1.05, mb: 0.5 }}
                              >
                                {companyNamePrimary}
                                {companyNameAccent && (
                                  <>
                                    {" "}
                                    <SoftBox component="span" sx={{ color: companyBrandColor }}>
                                      {companyNameAccent}
                                    </SoftBox>
                                  </>
                                )}
                              </SoftTypography>
                            )}
                            <SoftBox sx={companyHeaderLinesSx}>
                              {companyLocationLines.length > 0 && (
                                <SoftBox
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    gap: "4px",
                                    width: "100%",
                                  }}
                                >
                                  <Icon sx={companyHeaderIconSx}>location_on</Icon>
                                  <SoftTypography
                                    component="span"
                                    variant="button"
                                    fontWeight="regular"
                                    sx={{
                                      m: "0 !important",
                                      color: `${companyHeaderTextColor} !important`,
                                      fontSize: "11px",
                                      lineHeight: 1.45,
                                      textAlign: "center",
                                    }}
                                  >
                                    {companyLocationLines.map((line) => (
                                      <span
                                        key={line}
                                        style={{ display: "block", color: companyHeaderTextColor }}
                                      >
                                        {line}
                                      </span>
                                    ))}
                                  </SoftTypography>
                                </SoftBox>
                              )}
                              {(company.website || company.email) && (
                                <SoftBox
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexWrap: "nowrap",
                                    columnGap: "12px",
                                    width: "100%",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {company.website && (
                                    <SoftBox
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "3px",
                                      }}
                                    >
                                      <Icon sx={companyHeaderIconSx}>language</Icon>
                                      <SoftTypography
                                        component="span"
                                        variant="button"
                                        fontWeight="regular"
                                        sx={{
                                          color: `${companyHeaderTextColor} !important`,
                                          fontSize: "11px",
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {company.website}
                                      </SoftTypography>
                                    </SoftBox>
                                  )}
                                  {company.email && (
                                    <SoftBox
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "3px",
                                      }}
                                    >
                                      <Icon sx={companyHeaderIconSx}>email</Icon>
                                      <SoftTypography
                                        component="span"
                                        variant="button"
                                        fontWeight="regular"
                                        sx={{
                                          color: `${companyHeaderTextColor} !important`,
                                          fontSize: "11px",
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {company.email}
                                      </SoftTypography>
                                    </SoftBox>
                                  )}
                                </SoftBox>
                              )}
                              {company.phone && (
                                <SoftBox
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "4px",
                                    width: "100%",
                                  }}
                                >
                                  <Icon sx={companyHeaderIconSx}>phone</Icon>
                                  <SoftTypography
                                    component="span"
                                    variant="button"
                                    fontWeight="regular"
                                    sx={{
                                      color: `${companyHeaderTextColor} !important`,
                                      fontSize: "11px",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    Phone: {company.phone}
                                  </SoftTypography>
                                </SoftBox>
                              )}
                              <SoftBox
                                className="invoice-company-identity"
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                flexWrap="nowrap"
                                mt={0}
                                sx={{
                                  width: "100%",
                                  whiteSpace: "nowrap",
                                  "& > *": {
                                    fontSize: "10px !important",
                                    lineHeight: "1.25 !important",
                                    fontWeight: "400 !important",
                                  },
                                  "& > *:not(:last-child)": {
                                    borderRight: `1px solid ${invoiceBorder}`,
                                  },
                                }}
                              >
                                {company.gstin && (
                                  <SoftTypography
                                    variant="button"
                                    px={0.7}
                                    sx={{ color: "#333333" }}
                                  >
                                    GSTIN: {company.gstin}
                                  </SoftTypography>
                                )}
                                {company.vat_number && (
                                  <SoftTypography
                                    variant="button"
                                    px={0.7}
                                    sx={{ color: "#333333" }}
                                  >
                                    VAT Number: {company.vat_number}
                                  </SoftTypography>
                                )}
                                {company.pan && (
                                  <SoftTypography
                                    variant="button"
                                    px={0.7}
                                    sx={{ color: "#333333" }}
                                  >
                                    PAN: {company.pan}
                                  </SoftTypography>
                                )}
                                {company.msme_registration_no && (
                                  <SoftTypography
                                    variant="button"
                                    px={0.7}
                                    sx={{ color: "#333333" }}
                                  >
                                    MSME: {company.msme_registration_no}
                                  </SoftTypography>
                                )}
                                {(company.additional_company_fields || [])
                                  .filter(
                                    (item) =>
                                      !/hsn/i.test(String(item?.name || "")) &&
                                      valuePresent(item?.name) &&
                                      valuePresent(item?.value)
                                  )
                                  .map((item) => (
                                    <SoftTypography
                                      key={`${item.name}-${item.value}`}
                                      variant="button"
                                      px={0.7}
                                      sx={{ color: "#333333" }}
                                    >
                                      {item.name}: {item.value}
                                    </SoftTypography>
                                  ))}
                              </SoftBox>
                            </SoftBox>
                          </SoftBox>
                        )}
                      </td>
                      <td
                        className="invoice-print-header-qr"
                        style={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          width: "20%",
                        }}
                      >
                        {canViewQrCode && (
                          <SoftBox
                            className="invoice-print-header-qr-frame"
                            component={eInvoicePdfUrl ? "a" : "div"}
                            href={eInvoicePdfUrl || undefined}
                            target={eInvoicePdfUrl ? "_blank" : undefined}
                            rel={eInvoicePdfUrl ? "noopener noreferrer" : undefined}
                            aria-label={eInvoicePdfUrl ? "Open e-invoice PDF in a new tab" : undefined}
                            style={{
                              display: "inline-flex",
                              justifyContent: "center",
                              alignItems: "center",
                              margin: "0 auto",
                              padding: "2px",
                              border: `2px solid ${companyBrandColor}`,
                              lineHeight: 0,
                              verticalAlign: "middle",
                              cursor: eInvoicePdfUrl ? "pointer" : "default",
                            }}
                          >
                            {invoice.e_invoice_qr_code_url ? (
                              <img
                                src={invoice.e_invoice_qr_code_url}
                                alt={`E-invoice QR for ${invoice.invoice_number || invoice.id}`}
                                style={{
                                  display: "block",
                                  width: "97.5px",
                                  height: "97.5px",
                                  margin: "0",
                                }}
                              />
                            ) : effectiveQrValue ? (
                              <QRCodeSVG
                                value={effectiveQrValue}
                                size={97.5}
                                level="M"
                                includeMargin={false}
                                title={`Invoice ${invoice.invoice_number || invoice.id}`}
                                style={{
                                  display: "block",
                                  margin: "0",
                                }}
                              />
                            ) : null}
                          </SoftBox>
                        )}
                      </td>
                    </tr>
                    <tr className="invoice-print-header-rule">
                      <td colSpan={3} />
                    </tr>
                  </tbody>
                </table>

                <SoftBox
                  className={`invoice-screen-header invoice-header-grid invoice-header-with-qr${
                    company.logo ? " invoice-header-with-logo" : ""
                  }`}
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr",
                    md: company.logo ? "250px minmax(0, 1fr) 190px" : "minmax(0, 1fr) 190px",
                  }}
                  alignItems="start"
                  gap={0}
                  sx={{
                    position: "relative",
                    pb: "3px",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: "3px",
                      background: `linear-gradient(90deg, ${invoiceNavy} 0 42%, ${companyBrandColor} 42% 100%)`,
                    },
                  }}
                >
                  {canViewCompanyDetails && company.logo && (
                    <SoftBox
                      className="invoice-header-logo"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      pt={0}
                      pl={0}
                    >
                      <SoftBox
                        component="img"
                        src={company.logo}
                        alt={`${company.name || "Company"} logo`}
                        sx={{
                          width: "109.6875px",
                          height: "73.125px",
                          maxWidth: "100%",
                          objectFit: "contain",
                          objectPosition: "center",
                        }}
                      />
                    </SoftBox>
                  )}
                  {canViewCompanyDetails && (
                    <SoftBox
                      className="invoice-header-details"
                      sx={{ ...companyHeaderDetailsSx, py: 1, px: 1.25 }}
                    >
                      {company.name && (
                        <SoftTypography
                          variant="h3"
                          fontWeight="bold"
                          sx={{ color: invoiceNavy, lineHeight: 1.05, mb: 0.5 }}
                        >
                          {companyNamePrimary}
                          {companyNameAccent && (
                            <>
                              {" "}
                              <SoftBox component="span" sx={{ color: companyBrandColor }}>
                                {companyNameAccent}
                              </SoftBox>
                            </>
                          )}
                        </SoftTypography>
                      )}
                      <SoftBox sx={companyHeaderLinesSx}>
                        {companyLocationLines.length > 0 && (
                          <SoftBox
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              gap: "4px",
                              width: "100%",
                            }}
                          >
                            <Icon sx={companyHeaderIconSx}>location_on</Icon>
                            <SoftTypography
                              component="span"
                              variant="button"
                              fontWeight="regular"
                              sx={{
                                m: "0 !important",
                                color: `${companyHeaderTextColor} !important`,
                                fontSize: "11px",
                                lineHeight: 1.45,
                                textAlign: "center",
                              }}
                            >
                              {companyLocationLines.map((line) => (
                                <span
                                  key={line}
                                  style={{ display: "block", color: companyHeaderTextColor }}
                                >
                                  {line}
                                </span>
                              ))}
                            </SoftTypography>
                          </SoftBox>
                        )}
                        {(company.website || company.email) && (
                          <SoftBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexWrap: "nowrap",
                              columnGap: "12px",
                              width: "100%",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {company.website && (
                              <SoftBox
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "3px",
                                }}
                              >
                                <Icon sx={companyHeaderIconSx}>language</Icon>
                                <SoftTypography
                                  component="span"
                                  variant="button"
                                  fontWeight="regular"
                                  sx={{
                                    color: `${companyHeaderTextColor} !important`,
                                    fontSize: "11px",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {company.website}
                                </SoftTypography>
                              </SoftBox>
                            )}
                            {company.email && (
                              <SoftBox
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "3px",
                                }}
                              >
                                <Icon sx={companyHeaderIconSx}>email</Icon>
                                <SoftTypography
                                  component="span"
                                  variant="button"
                                  fontWeight="regular"
                                  sx={{
                                    color: `${companyHeaderTextColor} !important`,
                                    fontSize: "11px",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {company.email}
                                </SoftTypography>
                              </SoftBox>
                            )}
                          </SoftBox>
                        )}
                        {company.phone && (
                          <SoftBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                              width: "100%",
                            }}
                          >
                            <Icon sx={companyHeaderIconSx}>phone</Icon>
                            <SoftTypography
                              component="span"
                              variant="button"
                              fontWeight="regular"
                              sx={{
                                color: `${companyHeaderTextColor} !important`,
                                fontSize: "11px",
                                lineHeight: 1.4,
                              }}
                            >
                              Phone: {company.phone}
                            </SoftTypography>
                          </SoftBox>
                        )}
                        <SoftBox
                          className="invoice-company-identity"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          flexWrap="nowrap"
                          mt={0}
                          sx={{
                            width: "100%",
                            whiteSpace: "nowrap",
                            "& > *": {
                              fontSize: "10px !important",
                              lineHeight: "1.25 !important",
                              fontWeight: "400 !important",
                            },
                            "& > *:not(:last-child)": { borderRight: `1px solid ${invoiceBorder}` },
                          }}
                        >
                          {company.gstin && (
                            <SoftTypography variant="button" px={0.7} sx={{ color: "#333333" }}>
                              GSTIN: {company.gstin}
                            </SoftTypography>
                          )}
                          {company.vat_number && (
                            <SoftTypography variant="button" px={0.7} sx={{ color: "#333333" }}>
                              VAT Number: {company.vat_number}
                            </SoftTypography>
                          )}
                          {company.pan && (
                            <SoftTypography variant="button" px={0.7} sx={{ color: "#333333" }}>
                              PAN: {company.pan}
                            </SoftTypography>
                          )}
                          {company.msme_registration_no && (
                            <SoftTypography variant="button" px={0.7} sx={{ color: "#333333" }}>
                              MSME: {company.msme_registration_no}
                            </SoftTypography>
                          )}
                          {(company.additional_company_fields || [])
                            .filter(
                              (item) =>
                                !/hsn/i.test(String(item?.name || "")) &&
                                valuePresent(item?.name) &&
                                valuePresent(item?.value)
                            )
                            .map((item) => (
                              <SoftTypography
                                key={`${item.name}-${item.value}`}
                                variant="button"
                                px={0.7}
                                sx={{ color: "#333333" }}
                              >
                                {item.name}: {item.value}
                              </SoftTypography>
                            ))}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  )}
                  {canViewQrCode && (
                    <SoftBox
                      className="invoice-header-qr"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      p={0.375}
                      sx={{ boxSizing: "border-box" }}
                    >
                      <SoftBox
                        component={eInvoicePdfUrl ? "a" : "div"}
                        href={eInvoicePdfUrl || undefined}
                        target={eInvoicePdfUrl ? "_blank" : undefined}
                        rel={eInvoicePdfUrl ? "noopener noreferrer" : undefined}
                        aria-label={eInvoicePdfUrl ? "Open e-invoice PDF in a new tab" : undefined}
                        sx={{
                          display: "inline-flex",
                          lineHeight: 0,
                          border: `2px solid ${companyBrandColor}`,
                          p: "2px",
                          borderRadius: 0,
                          cursor: eInvoicePdfUrl ? "pointer" : "default",
                        }}
                      >
                        {invoice.e_invoice_qr_code_url ? (
                          <SoftBox
                            component="img"
                            src={invoice.e_invoice_qr_code_url}
                            alt={`E-invoice QR for ${invoice.invoice_number || invoice.id}`}
                            sx={{ width: 97.5, height: 97.5, objectFit: "contain" }}
                          />
                        ) : effectiveQrValue ? (
                          <QRCodeSVG
                            value={effectiveQrValue}
                            size={97.5}
                            level="M"
                            includeMargin={false}
                            title={`Invoice ${invoice.invoice_number || invoice.id}`}
                          />
                        ) : null}
                      </SoftBox>
                    </SoftBox>
                  )}
                </SoftBox>

                {invoiceType === "client" &&
                  canViewEInvoiceDetails &&
                  eInvoiceStatus !== "not_generated" && (
                  <SoftBox
                    className="invoice-no-print"
                    mt={1}
                    p={1}
                    sx={{
                      border: "1px solid #d8dee7",
                      borderLeft: `4px solid ${eInvoiceMeta.color}`,
                    }}
                  >
                    <SoftTypography variant="button" fontWeight="bold">
                      E-Invoice: {eInvoiceMeta.label}
                    </SoftTypography>
                    {invoice.e_invoice_irn && (
                      <SoftTypography variant="caption" display="block">
                        IRN: {invoice.e_invoice_irn}
                      </SoftTypography>
                    )}
                    {invoice.e_invoice_error && (
                      <SoftTypography variant="caption" color="error" display="block">
                        {invoice.e_invoice_error}
                      </SoftTypography>
                    )}
                  </SoftBox>
                )}

                <SoftBox
                  className="invoice-details-grid"
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
                  alignItems="stretch"
                  columnGap={{ xs: 1, md: "3%" }}
                  rowGap={1}
                  mt={1}
                >
                  <SoftBox sx={{ border: `1px solid ${invoiceBorder}` }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={sectionTitleSx}>
                      {isCreditNote ? "CREDIT TO CLIENT" : invoiceType === "vendor" ? "PO TO VENDOR" : `BILL TO ${partyLabel.toUpperCase()}`}
                    </SoftTypography>
                    <Table size="small">
                      <TableBody>
                        {billedPartyRows.map((row) => (
                          <TableRow key={`${row.label}-${row.value}`}>
                            <TableCell sx={{ ...labelCellSx, border: 0, py: 0.22 }}>
                              {row.label}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellSx,
                                width: "5%",
                                border: 0,
                                py: 0.22,
                                px: 0,
                                textAlign: "center",
                              }}
                            >
                              :
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellSx,
                                width: "59%",
                                border: 0,
                                py: 0.22,
                                overflowWrap: "anywhere",
                                fontWeight: 400,
                              }}
                            >
                              {row.field === "billing_email" ? (
                                <>
                                  <span className="invoice-billing-email-all">{row.value}</span>
                                  <span
                                    className="invoice-billing-email-first"
                                    style={{ display: "none" }}
                                  >
                                    {firstEmailAddress(row.value)}
                                  </span>
                                </>
                              ) : (
                                row.value
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </SoftBox>
                  <SoftBox sx={{ border: `1px solid ${invoiceBorder}` }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={sectionTitleSx}>
                      {invoiceType === "vendor" ? "PO INVOICE INFORMATION" : `${documentLabel.toUpperCase()} INFORMATION`}
                    </SoftTypography>
                    <Table size="small">
                      <TableBody>
                        {issuerDetailRows.map((row) => (
                          <TableRow key={`${row.label}-${row.value}`}>
                            <TableCell sx={{ ...labelCellSx, width: "42%", border: 0, py: 0.22 }}>
                              {row.label}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellSx,
                                width: "5%",
                                border: 0,
                                py: 0.22,
                                px: 0,
                                textAlign: "center",
                              }}
                            >
                              :
                            </TableCell>
                            <TableCell sx={{ ...cellSx, width: "53%", border: 0, py: 0.22 }}>
                              {row.value}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </SoftBox>
                </SoftBox>

                {canViewItems && visibleItemColumns.length > 0 && (
                  <TableContainer
                    component={Paper}
                    sx={{ boxShadow: "none", borderRadius: 0, mt: 1 }}
                  >
                    <Table size="small">
                      <TableHead
                        sx={{ display: "table-header-group", backgroundColor: invoiceNavy }}
                      >
                        <TableRow>
                          {visibleItemColumns.map((column) => (
                            <TableCell
                              key={column.field}
                              className={`invoice-item-col-${column.field}`}
                              align="center"
                              sx={{
                                ...cellSx,
                                width: column.width,
                                backgroundColor: invoiceNavy,
                                color: "#fff",
                                fontWeight: 500,
                                textTransform: "uppercase !important",
                                fontSize: "10px",
                              }}
                            >
                              {column.label.toUpperCase()}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceItems.map((item, index) => (
                          <TableRow key={item.id || index}>
                            {visibleItemColumns.map((column) => (
                              <TableCell
                                key={column.field}
                                className={`invoice-item-col-${column.field}`}
                                align={column.field === "item_amount" ? "right" : "center"}
                                sx={cellSx}
                              >
                                {column.value(item, index)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                        {invoiceItems.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={Math.max(visibleItemColumns.length, 1)}
                              align="center"
                              sx={cellSx}
                            >
                              No invoice items.
                            </TableCell>
                          </TableRow>
                        )}
                        {invoiceType === "client" &&
                          invoiceItems.length > 0 &&
                          (quantityColumnIndex >= 0 || amountColumnIndex >= 0) && (
                            <TableRow>
                              {visibleItemColumns.map((column, columnIndex) => {
                                const isQuantityLabel =
                                  quantityColumnIndex > 0 &&
                                  columnIndex === quantityColumnIndex - 1;
                                const isQuantityValue = columnIndex === quantityColumnIndex;
                                const isAmountLabel =
                                  amountColumnIndex > 0 && columnIndex === amountColumnIndex - 1;
                                const isAmountValue = columnIndex === amountColumnIndex;
                                let value = "";
                                if (isQuantityLabel) value = "TOTAL QTY";
                                if (isQuantityValue) value = totalQuantityDisplay;
                                if (isAmountLabel) value = "TOTAL (USD)";
                                if (isAmountValue) value = usdMoney(invoice.source_amount_usd);

                                return (
                                  <TableCell
                                    key={`invoice-total-${column.field}`}
                                    className={`invoice-item-col-${column.field}`}
                                    align={
                                      isQuantityLabel ||
                                      isQuantityValue ||
                                      isAmountLabel ||
                                      isAmountValue
                                        ? "right"
                                        : "center"
                                    }
                                    sx={{
                                      ...cellSx,
                                      color:
                                        isQuantityValue || isAmountValue
                                          ? companyBrandColor
                                          : "#2f2f2f",
                                      fontWeight:
                                        isQuantityLabel ||
                                        isQuantityValue ||
                                        isAmountLabel ||
                                        isAmountValue
                                          ? 700
                                          : 400,
                                    }}
                                  >
                                    {value}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <SoftBox
                  className="invoice-two-column-grid"
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
                  alignItems="start"
                  mt={1}
                  columnGap={{ xs: 1, md: "3%" }}
                  rowGap={1}
                >
                  <SoftBox display="flex" flexDirection="column" gap={1}>
                    {!isCreditNote &&
                      (invoiceType === "vendor" || canViewCompanyDetails) &&
                      canViewInvoiceField("bank_details") &&
                      bankRows.length > 0 && (
                        <SoftBox sx={{ border: `1px solid ${invoiceBorder}` }}>
                          <SoftTypography variant="caption" fontWeight="bold" sx={sectionTitleSx}>
                            BANK DETAILS
                          </SoftTypography>
                          <SoftBox
                            display="grid"
                            gridTemplateColumns="58px minmax(0, 1fr)"
                            alignItems="center"
                            px={0.8}
                            py={0.55}
                          >
                            <SoftBox display="flex" justifyContent="center" alignItems="center">
                              <Icon sx={{ color: invoiceNavy, fontSize: "34px !important" }}>
                                account_balance
                              </Icon>
                            </SoftBox>
                            <Table size="small">
                              <TableBody>
                                {bankRows.map(([label, value]) => (
                                  <TableRow key={`${label}-${value}`}>
                                    <TableCell
                                      sx={{
                                        ...labelCellSx,
                                        width: "39%",
                                        border: 0,
                                        py: 0.18,
                                        pl: 0,
                                      }}
                                    >
                                      {label}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        ...cellSx,
                                        width: "5%",
                                        border: 0,
                                        py: 0.18,
                                        px: 0,
                                        textAlign: "center",
                                      }}
                                    >
                                      :
                                    </TableCell>
                                    <TableCell
                                      sx={{ ...cellSx, width: "56%", border: 0, py: 0.18 }}
                                    >
                                      {value}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </SoftBox>
                        </SoftBox>
                      )}
                    {canViewAmountSummary && (
                      <SoftBox sx={{ border: `1px solid ${invoiceBorder}` }}>
                        <SoftTypography variant="caption" fontWeight="bold" sx={sectionTitleSx}>
                          AMOUNT IN WORDS
                        </SoftTypography>
                        <SoftBox
                          display="grid"
                          gridTemplateColumns="58px minmax(0, 1fr)"
                          alignItems="center"
                          px={0.8}
                          py={0.8}
                        >
                          <SoftBox display="flex" justifyContent="center" alignItems="center">
                            <SoftBox
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              sx={{
                                width: 34,
                                height: 34,
                                border: `2px solid ${companyBrandColor}`,
                                borderRadius: "50%",
                                color: companyBrandColor,
                              }}
                            >
                              <Icon sx={{ fontSize: "22px !important" }}>description</Icon>
                            </SoftBox>
                          </SoftBox>
                          <SoftTypography
                            variant="button"
                            fontWeight="regular"
                            sx={{
                              color: "#333333",
                              textTransform: "uppercase",
                              fontSize: "11px",
                              lineHeight: 1.45,
                            }}
                          >
                            {amountInWords(invoiceValue, currencyCode, !isCreditNote)}
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    )}
                  </SoftBox>
                  {canViewAmountSummary && (
                    <SoftBox
                      sx={{
                        width: "100%",
                        alignSelf: "start",
                        border: `1px solid ${invoiceBorder}`,
                      }}
                    >
                      <SoftTypography variant="caption" fontWeight="bold" sx={sectionTitleSx}>
                        CALCULATION SUMMARY
                      </SoftTypography>
                      {calculationSummaryRows.map((row) => (
                        <SoftBox
                          key={row.label}
                          minHeight={28}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          borderBottom={`1px solid ${invoiceBorder}`}
                          py={0.35}
                          px={0.8}
                        >
                          <SoftTypography
                            variant="button"
                            fontWeight={
                              ["Amount", "USD Amount"].includes(row.label) ||
                              row.label.startsWith("Total Tax") ||
                              row.label.startsWith("FINAL AMOUNT")
                                ? "bold"
                                : "regular"
                            }
                            sx={{
                              color: row.label.startsWith("FINAL AMOUNT")
                                ? companyBrandColor
                                : "#333333",
                            }}
                          >
                            {row.label}
                          </SoftTypography>
                          <SoftTypography
                            variant="button"
                            fontWeight={
                              ["Amount", "USD Amount"].includes(row.label) ||
                              row.label.startsWith("Total Tax") ||
                              row.label.startsWith("FINAL AMOUNT")
                                ? "bold"
                                : "regular"
                            }
                            textAlign="right"
                            sx={{
                              color: row.label.startsWith("FINAL AMOUNT")
                                ? companyBrandColor
                                : "#222222",
                            }}
                          >
                            {row.displayValue}
                          </SoftTypography>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  )}
                </SoftBox>

                <SoftBox
                  className="invoice-footer-grid"
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 0.85fr" }}
                  mt={1}
                  sx={{
                    borderTop: `1px solid ${invoiceNavy}`,
                    borderBottom: `1px solid ${invoiceBorder}`,
                  }}
                >
                  <SoftBox p={0.8} sx={{ borderRight: { md: `1px solid ${invoiceBorder}` } }}>
                    {canViewInvoiceField("notes") && (
                      <>
                        <SoftTypography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ ...sectionTitleSx, px: 0, mb: 0.6 }}
                        >
                          {isCreditNote ? "CREDIT NOTE INFORMATION" : invoiceType === "vendor" ? "INVOICE INSTRUCTIONS" : "PAYMENT INSTRUCTIONS"}
                        </SoftTypography>
                        {invoiceType === "vendor" && !isCreditNote ? (
                          [
                            "Please mention the PO number on the invoice.",
                            "Kindly raise the invoice as per the approved completes and agreed CPI mentioned in this Purchase Order.",
                            "Please share the invoice along with the relevant reconciliation/supporting details.",
                            "Payment will be processed as per the agreed payment terms after verification and approval.",
                          ].map((instruction) => (
                            <SoftBox
                              key={instruction}
                              display="grid"
                              gridTemplateColumns="16px minmax(0, 1fr)"
                              alignItems="start"
                              mb={0.25}
                            >
                              <SoftBox
                                component="span"
                                sx={{ color: companyBrandColor, fontSize: "10px", lineHeight: 1.35 }}
                              >
                                ◆
                              </SoftBox>
                              <SoftTypography
                                variant="button"
                                fontWeight="regular"
                                display="block"
                                sx={{ color: "#333333", fontSize: "10px", lineHeight: 1.35 }}
                              >
                                {instruction}
                              </SoftTypography>
                            </SoftBox>
                          ))
                        ) : (
                          <>
                            <SoftTypography
                              variant="button"
                              fontWeight="regular"
                              display="block"
                              sx={{ color: "#333333", fontSize: "10px", lineHeight: 1.35 }}
                            >
                              {isCreditNote
                                ? company.invoice_description || "This credit note adjusts the referenced invoice(s)."
                                : company.invoice_description ||
                                  "Please make the payment within the due date mentioned above."}
                            </SoftTypography>
                            {!isCreditNote && (
                              <SoftTypography
                                variant="button"
                                fontWeight="regular"
                                display="block"
                                mt={0.35}
                                sx={{ color: "#333333", fontSize: "10px", lineHeight: 1.35 }}
                              >
                                Kindly share the payment confirmation once the transaction is completed.
                              </SoftTypography>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </SoftBox>
                  <SoftBox p={0.8} sx={{ borderRight: { md: `1px solid ${invoiceBorder}` } }}>
                    {canViewCompanyDetails && (
                      <>
                        <SoftTypography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ ...sectionTitleSx, px: 0, mb: 0.6 }}
                        >
                          TERMS & JURISDICTION
                        </SoftTypography>
                        <SoftBox
                              display="grid"
                              gridTemplateColumns="16px minmax(0, 1fr)"
                              alignItems="start"
                            >
                              <SoftBox
                                component="span"
                                sx={{ color: companyBrandColor, fontSize: "10px", lineHeight: 1.35 }}
                              >
                                ◆
                              </SoftBox>
                              <SoftTypography
                                variant="button"
                                fontWeight="regular"
                                display="block"
                                sx={{
                                  color: "#333333",
                                  fontSize: "10px",
                                  lineHeight: 1.35,
                                  textAlign: "justify",
                                  textJustify: "inter-word",
                                }}
                              >
                                Any dispute of this delivery order will be subject to our {jurisdiction}{" "}
                                Jurisdiction.
                              </SoftTypography>
                        </SoftBox>
                        {!isCreditNote && !isVendorInvoice && <SoftBox
                              display="grid"
                              gridTemplateColumns="16px minmax(0, 1fr)"
                              alignItems="start"
                              mt={0.25}
                            >
                              <SoftBox
                                component="span"
                                sx={{ color: companyBrandColor, fontSize: "10px", lineHeight: 1.35 }}
                              >
                                ◆
                              </SoftBox>
                              <SoftTypography
                                variant="button"
                                fontWeight="regular"
                                display="block"
                                sx={{
                                  color: "#333333",
                                  fontSize: "10px",
                                  lineHeight: 1.35,
                                  textAlign: "justify",
                                  textJustify: "inter-word",
                                }}
                              >
                                Interest @ 18% p.a. will be charged on overdue amounts.
                              </SoftTypography>
                        </SoftBox>}
                      </>
                    )}
                  </SoftBox>
                  <SoftBox
                    p={0.8}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                  >
                    {canViewCompanyDetails &&
                      canViewInvoiceField("signature") &&
                      company.signature_image && (
                        <>
                          <SoftTypography
                            variant="caption"
                            fontWeight="regular"
                            sx={{ color: "#333333" }}
                          >
                            FOR {String(company.name || "COMPANY").toUpperCase()}
                          </SoftTypography>
                          <SoftBox
                            component="img"
                            src={company.signature_image}
                            alt="Authorized company signature"
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "60px",
                              objectFit: "contain",
                              my: 0.25,
                            }}
                          />
                          <SoftTypography
                            variant="caption"
                            fontWeight="regular"
                            sx={{ color: "#333333" }}
                          >
                            Authorised Signatory
                          </SoftTypography>
                        </>
                      )}
                  </SoftBox>
                </SoftBox>
                <SoftBox mt={0.7} py={0.5} textAlign="center">
                  <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    sx={{ color: companyBrandColor, fontSize: "10px" }}
                  >
                    This is a computer generated {documentLabelLower} and does not require a physical signature.
                  </SoftTypography>
                  <SoftTypography
                    display="block"
                    variant="button"
                    fontWeight="bold"
                    mt={0.2}
                    sx={{ color: companyBrandColor, fontSize: "11px" }}
                  >
                    Thank You For Your Business!
                  </SoftTypography>
                </SoftBox>
              </Card>

              {canUpdateWorkflow && (
                <Card className="invoice-no-print" sx={{ mt: 2, p: { xs: 1.5, md: 2 } }}>
                  <SoftTypography variant="h6" mb={1.5}>
                    {documentLabel} Status Management
                  </SoftTypography>
                  <SoftBox
                    display="grid"
                    gridTemplateColumns={{
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    }}
                    gap={1.5}
                    alignItems="end"
                  >
                    {canUpdateWorkflowField("status") && (
                      <SoftBox>
                        <SoftTypography component="label" variant="caption" fontWeight="medium">
                          Status
                        </SoftTypography>
                        <SoftSelect
                          value={selectedOption(statusOptions, workflowStatuses.status)}
                          options={statusOptions}
                          onChange={(option) => changeWorkflowStatus("status", option)}
                        />
                      </SoftBox>
                    )}
                    {canUpdateWorkflowField("payment_status") && (
                      <SoftBox>
                        <SoftTypography component="label" variant="caption" fontWeight="medium">
                          Payment Status
                        </SoftTypography>
                        <SoftSelect
                          value={selectedOption(
                            paymentStatusOptions,
                            workflowStatuses.payment_status
                          )}
                          options={paymentStatusOptions}
                          onChange={(option) => changeWorkflowStatus("payment_status", option)}
                        />
                      </SoftBox>
                    )}
                    {canUpdateWorkflowField("email_status") && (
                      <SoftBox>
                        <SoftTypography component="label" variant="caption" fontWeight="medium">
                          Email Status
                        </SoftTypography>
                        <SoftSelect
                          value={selectedOption(availableEmailStatusOptions, workflowStatuses.email_status)}
                          options={availableEmailStatusOptions}
                          onChange={(option) => changeWorkflowStatus("email_status", option)}
                        />
                      </SoftBox>
                    )}
                    <SoftButton
                      color="info"
                      disabled={workflowSaving || !workflowDirty}
                      onClick={updateWorkflowStatuses}
                      sx={{ minHeight: 42 }}
                    >
                      {workflowSaving ? "Updating..." : "Update Statuses"}
                    </SoftButton>
                  </SoftBox>
                </Card>
              )}
            </>
          )
        )}
      </SoftBox>
      <Footer />
      <Dialog
        open={generateDialog.open}
        onClose={() =>
          !eInvoiceBusy && setGenerateDialog({ open: false, inr_exchange_rate: "" })
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Generate E-Invoice</DialogTitle>
        <DialogContent>
          <SoftTypography variant="button" fontWeight="bold" display="block">
            Please verify the INR values before submitting this invoice to the e-invoice portal.
          </SoftTypography>
          <SoftBox
            mt={1.5}
            p={1.25}
            display="grid"
            gridTemplateColumns="minmax(0, 1fr) auto"
            gap={0.75}
            sx={{ border: "1px solid #d8dee7", borderRadius: "8px" }}
          >
            <SoftTypography variant="button">Invoice Number</SoftTypography>
            <SoftTypography variant="button" fontWeight="bold" textAlign="right">
              {invoice?.invoice_number || "-"}
            </SoftTypography>
            <SoftTypography variant="button">Source Amount (USD)</SoftTypography>
            <SoftTypography variant="button" fontWeight="bold" textAlign="right">
              {usdMoney(eInvoiceSourceAmountUsd)}
            </SoftTypography>
            {!isUsd && (
              <>
                <SoftTypography variant="button">Current Invoice Amount ({currencyCode})</SoftTypography>
                <SoftTypography variant="button" fontWeight="bold" textAlign="right">
                  {money(invoice?.total_amount, invoice)}
                </SoftTypography>
              </>
            )}
          </SoftBox>

          {useInvoiceInrRate ? (
            <SoftBox mt={1.5}>
              <SoftTypography variant="caption" display="block">
                USD to INR Exchange Rate (saved with this invoice)
              </SoftTypography>
              <SoftInput value={String(invoiceExchangeRate)} disabled />
            </SoftBox>
          ) : (
            <SoftBox mt={1.5}>
              <SoftTypography variant="caption" display="block" mb={0.5}>
                USD to INR Exchange Rate *
              </SoftTypography>
              <SoftInput
                type="text"
                placeholder="83.5000000"
                value={generateDialog.inr_exchange_rate}
                inputProps={{ inputMode: "decimal" }}
                onChange={(event) =>
                  setGenerateDialog((current) => ({
                    ...current,
                    inr_exchange_rate: event.target.value,
                  }))
                }
              />
              {generateDialog.inr_exchange_rate && !validEInvoiceInrRate && (
                <SoftTypography variant="caption" color="error" display="block" mt={0.4}>
                  Enter a positive exchange rate with up to 3 digits before and up to 7 digits after the decimal point.
                </SoftTypography>
              )}
            </SoftBox>
          )}

          <SoftBox
            mt={1.5}
            p={1.25}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ backgroundColor: "#f0fdf4", border: "1px solid #86efac", borderRadius: "8px" }}
          >
            <SoftTypography variant="button" fontWeight="bold">
              Converted E-Invoice Amount (INR)
            </SoftTypography>
            <SoftTypography variant="h6" color="success">
              {validEInvoiceInrRate ? <>₹{decimal(eInvoiceConvertedAmount)}</> : "—"}
            </SoftTypography>
          </SoftBox>
          <SoftTypography variant="caption" display="block" mt={1.25}>
            On confirmation, all e-invoice item and total values will be calculated from the
            original USD amounts using this USD-to-INR exchange rate. The original invoice
            currency and amount will not be changed.
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <SoftButton
            color="secondary"
            disabled={eInvoiceBusy}
            onClick={() => setGenerateDialog({ open: false, inr_exchange_rate: "" })}
          >
            Cancel
          </SoftButton>
          <SoftButton
            color="success"
            disabled={eInvoiceBusy || !validEInvoiceInrRate}
            onClick={generateEInvoice}
          >
            {eInvoiceBusy ? "Generating..." : "Confirm & Generate E-Invoice"}
          </SoftButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={cancelDialog.open}
        onClose={() => !eInvoiceBusy && setCancelDialog((current) => ({ ...current, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cancel E-Invoice</DialogTitle>
        <DialogContent>
          <SoftTypography variant="caption" display="block" mb={0.5}>
            Cancellation reason
          </SoftTypography>
          <SoftSelect
            options={eInvoiceCancelReasons}
            value={selectedOption(eInvoiceCancelReasons, cancelDialog.reason_code)}
            onChange={(option) =>
              setCancelDialog((current) => ({ ...current, reason_code: option?.value || "1" }))
            }
          />
          <SoftTypography variant="caption" display="block" mt={1.5} mb={0.5}>
            Remarks (required, maximum 100 characters)
          </SoftTypography>
          <SoftInput
            multiline
            minRows={3}
            value={cancelDialog.remarks}
            inputProps={{ maxLength: 100 }}
            onChange={(event) =>
              setCancelDialog((current) => ({ ...current, remarks: event.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <SoftButton
            color="secondary"
            disabled={eInvoiceBusy}
            onClick={() => setCancelDialog((current) => ({ ...current, open: false }))}
          >
            Close
          </SoftButton>
          <SoftButton
            color="error"
            disabled={eInvoiceBusy || !cancelDialog.remarks.trim()}
            onClick={cancelEInvoice}
          >
            {eInvoiceBusy ? "Cancelling..." : "Confirm Cancellation"}
          </SoftButton>
        </DialogActions>
      </Dialog>
      <SoftSnackbar
        color={workflowSnackbar.color}
        icon={workflowSnackbar.color === "success" ? "check" : "warning"}
        title={workflowSnackbar.color === "success" ? "Statuses Updated" : "Update Failed"}
        content={workflowSnackbar.message}
        open={workflowSnackbar.open}
        onClose={() => setWorkflowSnackbar((current) => ({ ...current, open: false }))}
        close={() => setWorkflowSnackbar((current) => ({ ...current, open: false }))}
      />
    </DashboardLayout>
  );
}

InvoiceView.propTypes = {
  invoiceType: PropTypes.oneOf(["client", "vendor"]).isRequired,
  documentType: PropTypes.oneOf(["invoice", "credit_note"]),
};

export default InvoiceView;
